<template>
  <div class="wysiwyg-editor">
    <div v-if="toolbar.length" class="wysiwyg-editor__toolbar">
      <div class="wysiwyg-editor__toolbar-left">
        <div
          v-for="(group, groupIndex) in toolbarLeftGroups"
          :key="`toolbar-group-${groupIndex}`"
          class="wysiwyg-editor__toolbar-group"
        >
          <template v-for="(item, itemIndex) in group">
            <v-menu
              v-if="isTextColorControl(item)"
              :key="`toolbar-text-color-${groupIndex}-${itemIndex}`"
              offset-y
              bottom
              :close-on-content-click="true"
            >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                type="button"
                icon
                small
                class="wysiwyg-editor__icon-btn"
                :color="getToolbarColor(item)"
                @mousedown.prevent.stop="saveSelection"
              >
                <v-icon :color="getToolbarColor(item)">mdi-format-color-text</v-icon>
              </v-btn>
            </template>

            <v-card class="wysiwyg-editor__color-menu">
              <v-btn
                text
                block
                class="wysiwyg-editor__automatic-btn"
                @click="applyTextColor('')"
              >
                Automatic
              </v-btn>

              <div class="wysiwyg-editor__color-grid">
                <button
                  v-for="option in textColorOptions"
                  :key="option.value"
                  type="button"
                  class="wysiwyg-editor__color-swatch"
                  :class="{ 'wysiwyg-editor__color-swatch--active': isCurrentTextColor(option.value) }"
                  :style="{ backgroundColor: option.value }"
                  :aria-label="`Text color ${option.label}`"
                  @click="applyTextColor(option.value)"
                ></button>
              </div>
            </v-card>
            </v-menu>

            <v-menu
              v-else-if="isHighlightControl(item)"
              :key="`toolbar-highlight-${groupIndex}-${itemIndex}`"
              offset-y
              bottom
              :close-on-content-click="true"
            >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                type="button"
                icon
                small
                class="wysiwyg-editor__icon-btn"
                :color="getToolbarColor(item)"
                @mousedown.prevent.stop="saveSelection"
              >
                <v-icon :color="getToolbarColor(item)">mdi-format-color-highlight</v-icon>
              </v-btn>
            </template>

            <v-card class="wysiwyg-editor__highlight-menu">
              <v-btn
                text
                block
                class="wysiwyg-editor__no-highlight-btn"
                @click="applyHighlight('')"
              >
                No highlight
              </v-btn>

              <div class="wysiwyg-editor__highlight-grid">
                <button
                  v-for="option in highlightOptions"
                  :key="option.value"
                  type="button"
                  class="wysiwyg-editor__highlight-swatch"
                  :class="{ 'wysiwyg-editor__highlight-swatch--active': isCurrentHighlight(option.value) }"
                  :style="{ backgroundColor: option.value }"
                  :aria-label="`Highlight ${option.label}`"
                  @click="applyHighlight(option.value)"
                ></button>
              </div>
            </v-card>
            </v-menu>

            <v-menu
              v-else-if="isFontSizeControl(item)"
              :key="`toolbar-font-size-${groupIndex}-${itemIndex}`"
              offset-y
              bottom
              :close-on-content-click="true"
            >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                type="button"
                icon
                small
                class="wysiwyg-editor__icon-btn"
                :color="getToolbarColor(item)"
                @mousedown.prevent.stop="saveSelection"
              >
                <v-icon :color="getToolbarColor(item)">mdi-format-size</v-icon>
              </v-btn>
            </template>

            <v-list dense class="wysiwyg-editor__font-size-menu">
              <v-list-item
                v-for="option in fontSizeOptions"
                :key="option.value"
                @click="applyFontSize(option.value)"
              >
                <v-list-item-title>{{ option.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
            </v-menu>

            <v-btn
              v-else
              :key="`toolbar-item-${groupIndex}-${itemIndex}`"
              type="button"
              icon
              small
              class="wysiwyg-editor__icon-btn"
              :color="getToolbarColor(item)"
              @mousedown.prevent.stop="saveSelection"
              @click="handleToolbarClick(item)"
            >
              <v-icon :color="getToolbarColor(item)">{{ getToolbarIcon(item) }}</v-icon>
            </v-btn>
          </template>

          <span
            v-if="groupIndex <= toolbarLeftGroups.length - 1"
            class="wysiwyg-editor__toolbar-separator"
            aria-hidden="true"
          >
            |
          </span>
        </div>
      </div>

      <div
        v-if="toolbarClearFormattingItem"
        class="wysiwyg-editor__toolbar-right"
      >

        <v-btn
          type="button"
          icon
          small
          class="wysiwyg-editor__icon-btn"
          :color="getToolbarColor(toolbarClearFormattingItem)"
          @mousedown.prevent.stop="saveSelection"
          @click="handleToolbarClick(toolbarClearFormattingItem)"
        >
          <v-icon :color="getToolbarColor(toolbarClearFormattingItem)">mdi-format-clear</v-icon>
        </v-btn>
      </div>
    </div>

    <div
      ref="editor"
      class="wysiwyg-editor__content"
      contenteditable="true"
      @input="onInput"
      @mouseup="saveSelection"
      @keyup="saveSelection"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'WysiwygEditor',

  props: {
    value: {
      type: String,
      default: ''
    },
    toolbar: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      savedRange: null,
      selectedFontSize: 'normal',
      toolbarStateVersion: 0,
      fontSizeOptions: [
        { label: 'Small', value: 'small' },
        { label: 'Normal', value: 'normal' },
        { label: 'Large', value: 'large' },
        { label: 'Extra Large', value: 'x-large' }
      ],
      highlightOptions: [
        { label: 'Rose', value: '#a45563' },
        { label: 'Red', value: '#a64b3f' },
        { label: 'Yellow', value: '#9b7900' },
        { label: 'Lime', value: '#768800' },
        { label: 'Green', value: '#177a56' },
        { label: 'Teal', value: '#2f7f82' },
        { label: 'Blue', value: '#4c6078' },
        { label: 'Purple', value: '#675264' }
      ],
      textColorOptions: [
        { label: 'White', value: '#ffffff' },
        { label: 'Red', value: '#f04854' },
        { label: 'Yellow', value: '#e1a500' },
        { label: 'Lime', value: '#99b400' },
        { label: 'Green', value: '#1ea36a' },
        { label: 'Teal', value: '#2b8f97' },
        { label: 'Blue', value: '#2d6bd4' },
        { label: 'Purple', value: '#ae4aa0' }
      ]
    }
  },

  mounted () {
    this.syncEditorValue()
    document.addEventListener('selectionchange', this.handleSelectionChange)
    this.updateToolbarStateFromSelection()
  },

  beforeDestroy () {
    document.removeEventListener('selectionchange', this.handleSelectionChange)
  },

  watch: {
    toolbar: {
      immediate: true,
      handler (newToolbar) {
        const sizeControl = this.getFontSizeControl(newToolbar)
        if (sizeControl && sizeControl.fontSize) {
          this.selectedFontSize = sizeControl.fontSize
        }
      }
    },
    value: {
      immediate: true,
      handler (newValue) {
        this.$nextTick(() => {
          this.syncEditorValue(newValue)
        })
      }
    }
  },

  computed: {
    toolbarLeftGroups () {
      return this.toolbar.filter((group) => !group.some((item) => this.isClearFormattingControl(item)))
    },

    toolbarClearFormattingItem () {
      for (const group of this.toolbar) {
        const clearFormattingItem = group.find((item) => this.isClearFormattingControl(item))
        if (clearFormattingItem) return clearFormattingItem
      }

      return null
    }
  },

  methods: {
    isFontSizeControl (item) {
      return item && Object.prototype.hasOwnProperty.call(item, 'fontSize')
    },

    isClearFormattingControl (item) {
      return item && Object.prototype.hasOwnProperty.call(item, 'clearFormatting')
    },

    isTextColorControl (item) {
      return item && Object.prototype.hasOwnProperty.call(item, 'textColor')
    },

    isHighlightControl (item) {
      return item && Object.prototype.hasOwnProperty.call(item, 'highlight')
    },

    isSelectionWithinEditor (selection = window.getSelection()) {
      const editor = this.$refs.editor
      if (!editor || !selection || !selection.anchorNode) return false

      const anchorNode = selection.anchorNode.nodeType === Node.ELEMENT_NODE
        ? selection.anchorNode
        : selection.anchorNode.parentElement

      return !!anchorNode && (editor === anchorNode || editor.contains(anchorNode))
    },

    getSelectionElement (selection = window.getSelection()) {
      if (!selection || !selection.anchorNode) return null

      return selection.anchorNode.nodeType === Node.ELEMENT_NODE
        ? selection.anchorNode
        : selection.anchorNode.parentElement
    },

    normalizeFontSize (fontSize) {
      const sizeMap = {
        '12px': 'small',
        '16px': 'normal',
        '20px': 'large',
        '28px': 'x-large'
      }

      return sizeMap[fontSize] || 'normal'
    },

    normalizeColorValue (colorValue) {
      if (!colorValue) return ''

      const color = String(colorValue).trim().toLowerCase()
      if (color.startsWith('#')) return color

      const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
      if (!rgbMatch) return color

      const toHex = (value) => Number(value).toString(16).padStart(2, '0')
      return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`
    },

    getCurrentFontSize () {
      const selection = window.getSelection && window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) {
        return 'normal'
      }

      let currentNode = this.getSelectionElement(selection)
      const editor = this.$refs.editor

      while (currentNode && currentNode !== editor) {
        const inlineSize = currentNode.style && currentNode.style.fontSize
        if (inlineSize) {
          return this.normalizeFontSize(inlineSize)
        }

        currentNode = currentNode.parentElement
      }

      return 'normal'
    },

    getCurrentHighlightColor () {
      const selection = window.getSelection && window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) {
        return ''
      }

      let currentNode = this.getSelectionElement(selection)
      const editor = this.$refs.editor

      while (currentNode && currentNode !== editor) {
        const backgroundColor = currentNode.style && currentNode.style.backgroundColor
        if (backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== 'initial' && backgroundColor !== 'inherit') {
          return this.normalizeColorValue(backgroundColor)
        }

        currentNode = currentNode.parentElement
      }

      return ''
    },

    getCurrentTextColor () {
      const selection = window.getSelection && window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) {
        return ''
      }

      let currentNode = this.getSelectionElement(selection)
      const editor = this.$refs.editor

      while (currentNode && currentNode !== editor) {
        const textColor = currentNode.style && currentNode.style.color
        if (textColor && textColor !== 'inherit' && textColor !== 'initial' && textColor !== 'currentcolor') {
          return this.normalizeColorValue(textColor)
        }

        currentNode = currentNode.parentElement
      }

      return ''
    },

    getCurrentAlignment () {
      const selection = window.getSelection && window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) {
        return 'left'
      }

      let currentNode = this.getSelectionElement(selection)
      const editor = this.$refs.editor

      while (currentNode && currentNode !== editor) {
        const textAlign = currentNode.style && currentNode.style.textAlign
          ? currentNode.style.textAlign
          : window.getComputedStyle(currentNode).textAlign

        if (textAlign && textAlign !== 'start' && textAlign !== 'left') {
          return textAlign
        }

        currentNode = currentNode.parentElement
      }

      return 'left'
    },

    isSelectionInsideTag (tagName) {
      const selection = window.getSelection && window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) {
        return false
      }

      const targetTag = String(tagName || '').toUpperCase()
      let currentNode = this.getSelectionElement(selection)
      const editor = this.$refs.editor

      while (currentNode && currentNode !== editor) {
        if (currentNode.tagName === targetTag) {
          return true
        }

        currentNode = currentNode.parentElement
      }

      return false
    },

    isSelectionWithinList (selection = window.getSelection()) {
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) {
        return false
      }

      let currentNode = this.getSelectionElement(selection)
      const editor = this.$refs.editor

      while (currentNode && currentNode !== editor) {
        if (currentNode.tagName === 'LI' || currentNode.tagName === 'UL' || currentNode.tagName === 'OL') {
          return true
        }

        currentNode = currentNode.parentElement
      }

      return false
    },

    isToolbarActive (item) {
      this.toolbarStateVersion

      if (this.isTextColorControl(item)) {
        return !!this.getCurrentTextColor()
      }

      if (this.isHighlightControl(item)) {
        return !!this.getCurrentHighlightColor()
      }

      if (this.isFontSizeControl(item)) {
        return this.selectedFontSize !== 'normal'
      }

      if (typeof item === 'string') {
        const commandStateMap = {
          bold: 'bold',
          italic: 'italic',
          underline: 'underline',
          strikethrough: 'strikeThrough'
        }

        if (commandStateMap[item]) {
          try {
            return document.queryCommandState(commandStateMap[item])
          } catch (error) {
            return false
          }
        }

        if (item === 'link') {
          return this.isSelectionInsideTag('A')
        }

        if (item === 'image') {
          return false
        }
      }

      if (item && Object.prototype.hasOwnProperty.call(item, 'align')) {
        const align = item.align || 'left'
        return this.getCurrentAlignment() === align
      }

      if (item && item.list) {
        const command = item.list === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList'

        try {
          return document.queryCommandState(command)
        } catch (error) {
          return false
        }
      }

      return false
    },

    getToolbarColor (item) {
      return this.isToolbarActive(item) ? 'primary lighten-1' : 'primary'
    },

    getToolbarIcon (item) {
      if (typeof item === 'string') {
        const mapping = {
          bold: 'mdi-format-bold',
          italic: 'mdi-format-italic',
          underline: 'mdi-format-underline',
          strikethrough: 'mdi-format-strikethrough-variant',
          link: 'mdi-link-variant',
          image: 'mdi-image'
        }

        return mapping[item] || 'mdi-format-text'
      }

      if (this.isFontSizeControl(item)) {
        return 'mdi-format-size'
      }

      if (this.isClearFormattingControl(item)) {
        return 'mdi-format-clear'
      }

      if (this.isTextColorControl(item)) {
        return 'mdi-format-color-text'
      }

      if (this.isHighlightControl(item)) {
        return 'mdi-format-color-highlight'
      }

      if (item && Object.prototype.hasOwnProperty.call(item, 'align')) {
        const mapping = {
          '': 'mdi-format-align-left',
          center: 'mdi-format-align-center',
          right: 'mdi-format-align-right'
        }

        return mapping[item.align] || 'mdi-format-align-left'
      }

      if (item && item.list) {
        return item.list === 'ordered' ? 'mdi-format-list-numbered' : 'mdi-format-list-bulleted'
      }

      return 'mdi-format-text'
    },

    saveSelection () {
      const selection = window.getSelection && window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) return

      this.savedRange = selection.getRangeAt(0).cloneRange()
      this.selectedFontSize = this.getCurrentFontSize()
      this.toolbarStateVersion += 1
    },

    stripBackgroundFromNode (node) {
      if (!node || node.nodeType !== Node.ELEMENT_NODE) return

      if (node.style) {
        node.style.backgroundColor = ''
      }

      Array.from(node.children || []).forEach((child) => {
        this.stripBackgroundFromNode(child)
      })
    },

    rangeIntersectsNode (range, node) {
      if (!range || !node) return false

      if (typeof range.intersectsNode === 'function') {
        try {
          return range.intersectsNode(node)
        } catch (error) {
          return false
        }
      }

      const nodeRange = document.createRange()
      try {
        nodeRange.selectNodeContents(node)
        return (
          range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0 &&
          range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0
        )
      } catch (error) {
        return false
      } finally {
        nodeRange.detach && nodeRange.detach()
      }
    },

    clearHighlightInRange (range) {
      const editor = this.$refs.editor
      if (!editor || !range) return

      const elements = editor.querySelectorAll('*')
      Array.from(elements).forEach((element) => {
        if (element.style && element.style.backgroundColor && this.rangeIntersectsNode(range, element)) {
          element.style.backgroundColor = ''
        }
      })
    },

    stripTextColorFromNode (node) {
      if (!node || node.nodeType !== Node.ELEMENT_NODE) return

      if (node.style) {
        node.style.color = ''
      }

      Array.from(node.children || []).forEach((child) => {
        this.stripTextColorFromNode(child)
      })
    },

    clearTextColorInRange (range) {
      const editor = this.$refs.editor
      if (!editor || !range) return

      const elements = editor.querySelectorAll('*')
      Array.from(elements).forEach((element) => {
        if (element.style && element.style.color && this.rangeIntersectsNode(range, element)) {
          element.style.color = ''
        }
      })
    },

    unwrapElement (element) {
      if (!element || !element.parentNode) return

      const parent = element.parentNode
      while (element.firstChild) {
        parent.insertBefore(element.firstChild, element)
      }

      parent.removeChild(element)
    },

    // Wraps each selected text node in its own styled span instead of extracting
    // the range, so selections spanning list items never split or duplicate <li>s
    wrapSelectedTextNodes (range, applyStyle) {
      const editor = this.$refs.editor
      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, null, false)
      const targets = []
      let node

      while ((node = walker.nextNode())) {
        if (!node.textContent.length || !this.rangeIntersectsNode(range, node)) continue

        const parentTag = node.parentNode && node.parentNode.tagName
        if ((parentTag === 'UL' || parentTag === 'OL') && !node.textContent.trim()) continue

        const start = node === range.startContainer ? range.startOffset : 0
        const end = node === range.endContainer ? range.endOffset : node.textContent.length
        if (start < end) targets.push({ node, start, end })
      }

      const spans = targets.map(({ node: textNode, start, end }) => {
        let target = textNode
        if (end < target.textContent.length) target.splitText(end)
        if (start > 0) target = target.splitText(start)

        const span = document.createElement('span')
        applyStyle(span)
        target.parentNode.insertBefore(span, target)
        span.appendChild(target)
        return span
      })

      if (!spans.length) return null

      const updatedRange = document.createRange()
      updatedRange.setStartBefore(spans[0])
      updatedRange.setEndAfter(spans[spans.length - 1])
      return updatedRange
    },

    getRangeTextOffsets (range) {
      const editor = this.$refs.editor
      const preRange = document.createRange()
      preRange.selectNodeContents(editor)
      preRange.setEnd(range.startContainer, range.startOffset)

      const start = preRange.toString().length
      return { start, end: start + range.toString().length }
    },

    setSelectionFromTextOffsets ({ start, end }) {
      const editor = this.$refs.editor
      const selection = window.getSelection && window.getSelection()
      if (!editor || !selection) return null

      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, null, false)
      const range = document.createRange()
      let position = 0
      let startSet = false
      let lastNode = null
      let node

      while ((node = walker.nextNode())) {
        const length = node.textContent.length
        if (!startSet && start <= position + length) {
          range.setStart(node, start - position)
          startSet = true
        }
        if (startSet && end <= position + length) {
          range.setEnd(node, end - position)
          selection.removeAllRanges()
          selection.addRange(range)
          return range
        }
        position += length
        lastNode = node
      }

      if (!startSet) return null
      range.setEnd(lastNode, lastNode.textContent.length)
      selection.removeAllRanges()
      selection.addRange(range)
      return range
    },

    removeListsInSelection () {
      const editor = this.$refs.editor

      // execCommand list toggles only remove a list when every selected line is
      // already in that list type, so repeat until no list intersects the selection
      for (let attempt = 0; attempt < 5; attempt++) {
        const selection = window.getSelection()
        if (!selection || !selection.rangeCount) return

        const range = selection.getRangeAt(0)
        const list = Array.from(editor.querySelectorAll('ol, ul')).find((element) => this.rangeIntersectsNode(range, element))
        if (!list) return

        const htmlBefore = editor.innerHTML
        document.execCommand(list.tagName === 'OL' ? 'insertOrderedList' : 'insertUnorderedList', false, null)
        if (editor.innerHTML === htmlBefore) return
      }
    },

    clearFormattingInRange (range) {
      const editor = this.$refs.editor
      if (!editor || !range) return

      this.removeListsInSelection()

      const currentSelection = window.getSelection()
      if (!currentSelection || !currentSelection.rangeCount) return

      const currentRange = currentSelection.getRangeAt(0)
      const offsets = this.getRangeTextOffsets(currentRange)

      // Snapshot the affected elements before mutating, then process innermost
      // first so unwrapping a node never hides the ones still to be cleaned
      const elements = Array.from(editor.querySelectorAll('*'))
        .filter((element) => this.rangeIntersectsNode(currentRange, element))
        .reverse()

      const inlineTags = ['B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'A', 'FONT']

      elements.forEach((element) => {
        if (element.style) {
          element.style.color = ''
          element.style.backgroundColor = ''
          element.style.fontSize = ''
          element.style.textAlign = ''
          if (!element.getAttribute('style')) element.removeAttribute('style')
        }

        if (inlineTags.includes(element.tagName)) {
          this.unwrapElement(element)
          return
        }

        if (element.tagName === 'SPAN') {
          element.removeAttribute('style')

          if (!element.attributes.length) {
            this.unwrapElement(element)
          }
        }
      })

      editor.normalize()
      this.setSelectionFromTextOffsets(offsets)

      try {
        document.execCommand('removeFormat', false, null)
      } catch (error) {
        // ignore browser differences
      }

      const restoredRange = this.setSelectionFromTextOffsets(offsets)
      this.savedRange = restoredRange ? restoredRange.cloneRange() : null
    },

    applyTextColor (textColor) {
      const editor = this.$refs.editor
      if (!editor) return

      this.restoreSelection()
      editor.focus()

      const selection = window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) return

      const range = selection.getRangeAt(0)
      if (range.collapsed) return

      if (textColor && this.isSelectionWithinList(selection)) {
        try {
          document.execCommand('styleWithCSS', false, true)
        } catch (error) {
          // ignore browser differences
        }

        try {
          document.execCommand('foreColor', false, textColor)
        } catch (error) {
          // fall back to the manual path below if execCommand is unavailable
        }

        this.savedRange = range.cloneRange()
        this.onInput()
        this.toolbarStateVersion += 1
        return
      }

      if (textColor) {
        const updatedRange = this.wrapSelectedTextNodes(range, (span) => { span.style.color = textColor })
        if (!updatedRange) return

        selection.removeAllRanges()
        selection.addRange(updatedRange)
        this.savedRange = updatedRange.cloneRange()
      } else {
        this.clearTextColorInRange(range)
        selection.removeAllRanges()
        selection.addRange(range)
        this.savedRange = range.cloneRange()
      }

      this.onInput()
      this.toolbarStateVersion += 1
    },

    isCurrentTextColor (colorValue) {
      return this.normalizeColorValue(this.getCurrentTextColor()) === this.normalizeColorValue(colorValue)
    },

    applyHighlight (highlightColor) {
      const editor = this.$refs.editor
      if (!editor) return

      this.restoreSelection()
      editor.focus()

      const selection = window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) return

      const range = selection.getRangeAt(0)
      if (range.collapsed) return

      if (this.isSelectionWithinList(selection)) {
        try {
          document.execCommand('styleWithCSS', false, true)
        } catch (error) {
          // ignore browser differences
        }

        if (highlightColor) {
          try {
            document.execCommand('hiliteColor', false, highlightColor)
          } catch (error) {
            try {
              document.execCommand('backColor', false, highlightColor)
            } catch (fallbackError) {
              // fall back to manual handling below if execCommand is unavailable
            }
          }
        } else {
          try {
            document.execCommand('hiliteColor', false, 'transparent')
          } catch (error) {
            try {
              document.execCommand('backColor', false, 'transparent')
            } catch (fallbackError) {
              // fall back to manual handling below if execCommand is unavailable
            }
          }
        }

        this.savedRange = range.cloneRange()
        this.onInput()
        this.toolbarStateVersion += 1
        return
      }

      if (highlightColor) {
        const updatedRange = this.wrapSelectedTextNodes(range, (span) => { span.style.backgroundColor = highlightColor })
        if (!updatedRange) return

        selection.removeAllRanges()
        selection.addRange(updatedRange)
        this.savedRange = updatedRange.cloneRange()
      } else {
        this.clearHighlightInRange(range)
        selection.removeAllRanges()
        selection.addRange(range)
        this.savedRange = range.cloneRange()
      }

      this.onInput()
      this.toolbarStateVersion += 1
    },

    isCurrentHighlight (colorValue) {
      return this.normalizeColorValue(this.getCurrentHighlightColor()) === this.normalizeColorValue(colorValue)
    },

    restoreSelection () {
      const editor = this.$refs.editor
      if (!editor || !this.savedRange) return false

      const selection = window.getSelection && window.getSelection()
      if (!selection) return false

      selection.removeAllRanges()
      selection.addRange(this.savedRange)
      return true
    },

    handleSelectionChange () {
      if (!this.isSelectionWithinEditor()) {
        this.selectedFontSize = 'normal'
        this.toolbarStateVersion += 1
        return
      }

      this.saveSelection()
    },

    updateToolbarStateFromSelection () {
      this.selectedFontSize = this.getCurrentFontSize()
      this.toolbarStateVersion += 1
    },

    getFontSizeControl (toolbar = []) {
      for (const group of toolbar) {
        if (!Array.isArray(group)) continue

        const match = group.find((item) => this.isFontSizeControl(item))
        if (match) return match
      }

      return null
    },

    syncEditorValue (nextValue = this.value) {
      const editor = this.$refs.editor
      if (!editor) return

      const currentHtml = editor.innerHTML || ''
      if (currentHtml !== (nextValue || '')) {
        editor.innerHTML = nextValue || ''
      }
    },

    onInput () {
      const html = this.$refs.editor.innerHTML
      this.$emit('input', html)
      this.updateToolbarStateFromSelection()
    },

    applyFontSize (fontSize) {
      const sizeMap = {
        small: '12px',
        normal: '16px',
        large: '20px',
        'x-large': '28px'
      }

      const editor = this.$refs.editor
      if (!editor) return

      this.restoreSelection()
      editor.focus()

      const selection = window.getSelection()
      if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) return

      const range = selection.getRangeAt(0)
      if (range.collapsed) {
        this.selectedFontSize = fontSize || 'normal'
        this.toolbarStateVersion += 1
        return
      }

      const sizeValue = sizeMap[fontSize] || sizeMap.normal
      const updatedRange = this.wrapSelectedTextNodes(range, (span) => { span.style.fontSize = sizeValue })
      if (!updatedRange) return

      selection.removeAllRanges()
      selection.addRange(updatedRange)

      this.savedRange = updatedRange.cloneRange()
      this.selectedFontSize = fontSize || 'normal'
      this.onInput()
    },

    handleToolbarClick (item) {
      const editor = this.$refs.editor
      if (!editor) return

      this.restoreSelection()
      editor.focus()

      if (typeof item === 'string') {
        switch (item) {
          case 'bold':
            document.execCommand('bold', false, null)
            break
          case 'italic':
            document.execCommand('italic', false, null)
            break
          case 'underline':
            document.execCommand('underline', false, null)
            break
          case 'strikethrough':
            document.execCommand('strikeThrough', false, null)
            break
          case 'link': {
            const url = window.prompt('Enter URL', 'https://')
            if (!url) return
            document.execCommand('createLink', false, url)
            break
          }
          case 'image': {
            const imageUrl = window.prompt('Enter image URL', 'https://')
            if (!imageUrl) return
            document.execCommand('insertImage', false, imageUrl)
            break
          }
        }
      } else if (item && Object.prototype.hasOwnProperty.call(item, 'clearFormatting')) {
        const selection = window.getSelection && window.getSelection()
        if (!selection || !selection.rangeCount || !this.isSelectionWithinEditor(selection)) return

        const range = selection.getRangeAt(0)
        if (range.collapsed) return

        this.clearFormattingInRange(range)
      } else if (item && Object.prototype.hasOwnProperty.call(item, 'align')) {
        const align = item.align || 'left'
        let command = 'justifyLeft'

        if (align === 'center') command = 'justifyCenter'
        if (align === 'right') command = 'justifyRight'

        document.execCommand(command, false, null)
      } else if (item && item.list) {
        if (item.list === 'ordered') {
          document.execCommand('insertOrderedList', false, null)
        } else {
          document.execCommand('insertUnorderedList', false, null)
        }
      }

      this.onInput()
      this.updateToolbarStateFromSelection()
    }
  }
}
</script>

<style scoped>
.wysiwyg-editor {
  width: 100%;
}

.wysiwyg-editor__toolbar {
  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 6px;
  margin-bottom: 8px;
}

.wysiwyg-editor__toolbar-left,
.wysiwyg-editor__toolbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.wysiwyg-editor__toolbar-left {
  flex: 1 1 auto;
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
  white-space: nowrap;
}

.wysiwyg-editor__toolbar-right {
  flex: 0 0 auto;
  margin-left: auto;
}

.wysiwyg-editor__toolbar-group {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.wysiwyg-editor__toolbar-group:not(:last-child)::after {
  content: none;
}

.wysiwyg-editor__toolbar-separator {
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.38);
  font-size: 18px;
  line-height: 1;
  margin: 0 6px;
  user-select: none;
}

.wysiwyg-editor__toolbar-separator--right {
  margin-left: 0;
  margin-right: 10px;
}

.wysiwyg-editor__font-size-group {
  display: inline-flex;
  align-items: center;
}

.wysiwyg-editor__font-size-menu {
  min-width: 160px;
}

.wysiwyg-editor__color-menu {
  padding: 8px;
  min-width: 196px;
  background: #2a2a2a;
}

.wysiwyg-editor__automatic-btn {
  margin-bottom: 8px;
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.wysiwyg-editor__color-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.wysiwyg-editor__color-swatch {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.wysiwyg-editor__color-swatch--active {
  box-shadow: 0 0 0 2px #fff, inset 0 0 0 1px rgba(0, 0, 0, 0.12);
}

.wysiwyg-editor__highlight-menu {
  padding: 8px;
  min-width: 196px;
  background: #2a2a2a;
}

.wysiwyg-editor__no-highlight-btn {
  margin-bottom: 8px;
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.wysiwyg-editor__highlight-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.wysiwyg-editor__highlight-swatch {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.wysiwyg-editor__highlight-swatch--active {
  box-shadow: 0 0 0 2px #fff, inset 0 0 0 1px rgba(0, 0, 0, 0.12);
}

.wysiwyg-editor__icon-btn {
  width: 30px;
  height: 30px;
}

.wysiwyg-editor__icon-btn .v-icon {
  transition: color 0.15s ease-in-out;
}

.wysiwyg-editor__icon-btn:hover {
  transform: translateY(-1px);
}

.wysiwyg-editor__icon-btn:focus::before {
  opacity: 0.06;
}

.wysiwyg-editor__content {
  width: 100%;
  min-height: 250px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
  line-height: 1.5;
  overflow: auto;
}

.wysiwyg-editor__content:empty::before {
  content: 'Start typing...';
  color: #94a3b8;
}
</style>



// WEBPACK FOOTER //
// src/components/WysiwygEditor.vue